import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";

// APIs
import { getPayPlansByPositions, IPayPlansByPositions } from "@/PayPlans/api/positions/queries";

// Helpers
import { hasViewAccess } from "@/PayPlans/helpers/Access";

// Subscriptions
import { getAssignmentsSubscriptions } from "./subscriptions.ts";

// Contexts
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Hooks
import useCacheCleaner from "@/Common/hooks/CacheCleaner";
import { useManagedPositions } from "@/Common/hooks/Positions";
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useTermsAndConditionEdition } from "@/PayPlans/hooks/useTermsAndCondition";

// Helpers
import { and } from "@Common/helpers/sonar";

// Components
import Assignments from "@/PayPlans/Layouts/Assignments";
import GroupContent from "@/PayPlans/components/Assignments/Contents/Group";

// Types
import { RootState } from "@/redux/store";

type TreeItem = {
	_id: string;
	title: string;
	bold: boolean;
	disabled: boolean;
	list: {
		_id: string;
		title: string;
		pdfAvailability: boolean;
		disabled: boolean;
		position: string;
	}[];
	position: string;
};

type SelectedItem = Omit<IPayPlansByPositions, "name" | "available"> & { title: string; disabled: boolean };

const groupPayPlans = (payPlans: IPayPlansByPositions[] = []) => {
	const positions = payPlans.map((item) => item.position);
	const uniquePositions = [...new Set(positions)];
	const initial: Record<string, IPayPlansByPositions[]> = {};
	return uniquePositions.reduce((acc, position) => {
		const positionPayPlans = payPlans.filter((item) => item.position === position);
		acc[position] = positionPayPlans.map((item) => ({
			_id: item._id,
			title: item.name,
			pdfAvailability: item.pdfAvailability,
			position,
		})) as unknown as IPayPlansByPositions[];
		return acc;
	}, initial);
};

function Group() {
	const { clearCache } = useCacheCleaner();
	const { isPresentAccess } = useAuthorization();
	const { addSubscription, deleteSubscription } = useSubscriptions();

	const { payPlanAvailability } = useTermsAndConditionEdition("");
	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId;

	// Local State
	const [selected, setSelected] = useState<SelectedItem | null>(null);
	const [dataTree, setDataTree] = useState<TreeItem[]>([]);

	/********************************************* QUERIES ************************************************/
	const { data: positions, isLoading: isLoadingPositions } = useManagedPositions({
		queryKeys: ["managedPositionsGroupPayPlan", locationId],
		// @ts-ignore
		onSuccess: (res: string[]) => res.filter((position) => hasViewAccess(position, isPresentAccess)),
	});

	const { data: tree, isLoading } = useQuery({
		queryKey: ["groupPayPlanAssignmentList", locationId],
		queryFn: async () => {
			const payPlans = await getPayPlansByPositions(locationId as string, positions as string[]);
			if (!payPlans) {
				return [];
			}
			type GroupedPayPlans = Record<string, IPayPlansByPositions[]>;
			const grouped: GroupedPayPlans = groupPayPlans(payPlans);

			return Object.entries(grouped).map(([position, value]) => ({
				_id: position,
				title: position,
				bold: true,
				disabled: !value.length,
				list: value.map((item) => ({ ...item, disabled: false })),
				position,
			})) as unknown as TreeItem[];
		},
		enabled: and(!!locationId, !!positions?.length),
		staleTime: Infinity,
	});

	/********************************************* HANDLERS ************************************************/
	const handleSelection = (selection: SelectedItem) => {
		if (selection) {
			addSubscription(
				["groupPayPlanAssignmentUsersByPosition", locationId, selection.position, selection.title],
				getAssignmentsSubscriptions()
			);
			setSelected(selection);
		}
	};

	useEffect(() => {
		if (tree) {
			setDataTree(tree);
		}

		return () => {
			setDataTree([]);
		};
	}, [tree]);

	useEffect(() => {
		if (payPlanAvailability) {
			const { pdfAvailability, _id, position, locationId: payPlanLocationId } = payPlanAvailability;
			if (payPlanLocationId === locationId) {
				const disabledDataTree = (treeNode: TreeItem) => {
					if (treeNode._id !== position) {
						return treeNode;
					}
					return {
						...treeNode,
						list: treeNode.list.map((listItem) => {
							let pdfAvailabilityAux = listItem.pdfAvailability;
							if (!_id || `${listItem.position}_${listItem.title}_${_id}` === listItem._id) {
								pdfAvailabilityAux = pdfAvailability;
							}
							return {
								...listItem,
								disabled: !pdfAvailabilityAux,
								pdfAvailability: pdfAvailabilityAux,
							};
						}),
					};
				};

				setDataTree((previousDataTree) => previousDataTree.map(disabledDataTree));
				setSelected(null);
			}
		}
	}, [payPlanAvailability]);

	useEffect(() => {
		return () => {
			setSelected(null);
			deleteSubscription(["groupPayPlanAssignmentUsersByPosition", locationId, selected?.position, selected?.title]);
			clearCache(["assignSelectedPlanButton"]);
		};
	}, [location, deleteSubscription]);

	return (
		<Assignments
			tree={dataTree}
			selected={selected}
			loading={isLoading || isLoadingPositions}
			isLocationAvailable={Boolean(locationId)}
			emptyTitle={"No Pay Plan selected"}
			onActionSelected={handleSelection}
		>
			<GroupContent selectedItem={selected} />
		</Assignments>
	);
}

export default Group;
